/**
 * Boot Pullback (Valid Pullback Flow) — constantes y resolución de config.
 *
 * SAFE_DEFAULTS es la única fuente de verdad de defaults; todo consumo de
 * config pasa por resolveConfig() para que ninguna clave llegue undefined
 * aunque la config persistida sea parcial o corrupta.
 */

export const QUANTUM_COLORS = Object.freeze({
  bull: '#1B5E20',
  bear: '#B71C1C',
  accent: '#B08D57',
  neutral: '#374151',
})

export const SAFE_DEFAULTS = Object.freeze({
  _version: 1,

  // ── Activación (la puerta real es activeRegistry; esto solo persiste el deseo)
  enabled: false,

  // ── Lógica ──────────────────────────────────────────────────────────────
  triggerOnWick: true,
  internalPivotLen: 2,
  maxFrozenLevels: 60,

  // ── Estructura externa ───────────────────────────────────────────────────
  showExternalStructure: true,
  showExtLevels: true,
  showExtMid: true,
  showShading: true,
  showLabels: true,
  labelOffsetX: 0,

  // ── Estructura interna ───────────────────────────────────────────────────
  showInternalStructure: false,
  showInternalZigzag: true,
  showIntLevels: true,

  // ── Marcadores ───────────────────────────────────────────────────────────
  showSwingMarkers: true,
  debugAnchors: false,

  // ── Tabla de estado ──────────────────────────────────────────────────────
  showStateTable: false,
  stateTablePosition: 'top-right',
  stateTableTextSize: 'small',

  // ── Multitemporal ────────────────────────────────────────────────────────
  showMultiTimeframe: false,
  htfOverride: 'auto',

  // ── Estilo Quantum ───────────────────────────────────────────────────────
  colorBull: QUANTUM_COLORS.bull,
  colorBear: QUANTUM_COLORS.bear,
  colorAccent: QUANTUM_COLORS.accent,
  colorNeutral: QUANTUM_COLORS.neutral,

  // ── Dashboard ────────────────────────────────────────────────────────────
  showDashboard: true,
  dashboardMinimized: false,
  badgePosition: { x: 8, y: 8 },
  panelSections: {},
})

export const LABEL_FONT = '10px -apple-system, BlinkMacSystemFont, "Trebuchet MS", Roboto, Ubuntu, sans-serif'

export const MTF_TIMEFRAMES = Object.freeze([
  { value: '1m', label: '1m', seconds: 60 },
  { value: '5m', label: '5m', seconds: 300 },
  { value: '15m', label: '15m', seconds: 900 },
  { value: '1h', label: '1h', seconds: 3600 },
  { value: '4h', label: '4h', seconds: 14400 },
  { value: '1d', label: '1D', seconds: 86400 },
  { value: '1w', label: '1W', seconds: 604800 },
  { value: '1M', label: '1M', seconds: 2592000 },
])

// Timeframe inmediatamente superior (para la fila "Dirección HTF").
export const HTF_MAP = Object.freeze({
  '1m': '5m',
  '5m': '15m',
  '10m': '15m',
  '15m': '1h',
  '30m': '1h',
  '1h': '4h',
  '2h': '4h',
  '4h': '1d',
  '1d': '1w',
  '1w': '1M',
  '1M': '1M',
})

const TABLE_POSITIONS = ['top-left', 'top-right', 'bottom-left', 'bottom-right']
const TABLE_SIZES = ['small', 'medium', 'large']

function clampNumber(v, min, max, fallback) {
  const n = typeof v === 'string' ? Number(v) : v
  if (!Number.isFinite(n)) return fallback
  return Math.min(max, Math.max(min, n))
}

function asColor(v, fallback) {
  return typeof v === 'string' && v.length > 0 ? v : fallback
}

function asBool(v, fallback) {
  return v == null ? fallback : Boolean(v)
}

/**
 * Merge defensivo: defaults + config persistida + saneado por clave.
 * Nunca devuelve undefined en ninguna clave conocida.
 */
export function resolveConfig(config) {
  const merged = { ...SAFE_DEFAULTS, ...(config || {}) }
  return {
    ...merged,
    enabled: asBool(merged.enabled, false),
    triggerOnWick: merged.triggerOnWick == null ? true : Boolean(merged.triggerOnWick),
    internalPivotLen: clampNumber(merged.internalPivotLen, 1, 5, 2),
    maxFrozenLevels: clampNumber(merged.maxFrozenLevels, 1, 500, 60),
    labelOffsetX: clampNumber(merged.labelOffsetX, -200, 200, 0),
    showExternalStructure: asBool(merged.showExternalStructure, true),
    showExtLevels: asBool(merged.showExtLevels, true),
    showExtMid: asBool(merged.showExtMid, true),
    showShading: asBool(merged.showShading, true),
    showLabels: asBool(merged.showLabels, true),
    showInternalStructure: asBool(merged.showInternalStructure, false),
    showInternalZigzag: asBool(merged.showInternalZigzag, true),
    showIntLevels: asBool(merged.showIntLevels, true),
    showSwingMarkers: asBool(merged.showSwingMarkers, true),
    debugAnchors: asBool(merged.debugAnchors, false),
    showStateTable: asBool(merged.showStateTable, false),
    stateTablePosition: TABLE_POSITIONS.includes(merged.stateTablePosition) ? merged.stateTablePosition : 'top-right',
    stateTableTextSize: TABLE_SIZES.includes(merged.stateTableTextSize) ? merged.stateTableTextSize : 'small',
    showMultiTimeframe: asBool(merged.showMultiTimeframe, false),
    htfOverride: typeof merged.htfOverride === 'string' && merged.htfOverride ? merged.htfOverride : 'auto',
    colorBull: asColor(merged.colorBull, QUANTUM_COLORS.bull),
    colorBear: asColor(merged.colorBear, QUANTUM_COLORS.bear),
    colorAccent: asColor(merged.colorAccent, QUANTUM_COLORS.accent),
    colorNeutral: asColor(merged.colorNeutral, QUANTUM_COLORS.neutral),
    showDashboard: asBool(merged.showDashboard, true),
    dashboardMinimized: asBool(merged.dashboardMinimized, false),
    badgePosition: merged.badgePosition && Number.isFinite(merged.badgePosition.x)
      ? merged.badgePosition
      : { x: 8, y: 8 },
    panelSections: merged.panelSections && typeof merged.panelSections === 'object' ? merged.panelSections : {},
  }
}
